import { useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../i18n';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { 
  LayoutDashboard, Users, FileText, Image, CheckSquare, 
  Search, Settings, LogOut, ChevronLeft, ChevronRight, Globe,
  Menu, Shield, Building2
} from 'lucide-react';

const MainLayout = () => {
  const { user, logout, updateLanguage } = useAuth();
  const t = useTranslation(user?.language || 'ja');
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: t.nav.dashboard },
    { path: '/contacts', icon: Users, label: t.nav.contacts },
    { path: '/companies', icon: Building2, label: user?.language === 'ja' ? '会社管理' : 'Companies' },
    { path: '/files', icon: FileText, label: t.nav.files },
    { path: '/tasks', icon: CheckSquare, label: t.nav.tasks },
    { path: '/search', icon: Search, label: t.nav.search },
    ...(user?.role === 'admin' ? [{ path: '/users', icon: Shield, label: user?.language === 'ja' ? 'ユーザー管理' : 'Users' }] : []),
  ];

  const handleLanguageChange = (lang) => {
    updateLanguage(lang);
  };

  const handleLogout = () => {
    logout();
  };

  const isActive = (path) => location.pathname === path;

  const NavContent = () => (
    <>
      {/* Logo */}
      <div className={`p-4 border-b border-border ${collapsed ? 'text-center' : ''}`}>
        <Link to="/dashboard" className="flex items-center gap-2">
          <img src="/logo.png" alt="RK File Control" className="w-8 h-8 object-contain" />
          {!collapsed && (
            <span className="text-lg font-bold text-glow">RK File Control</span>
          )}
        </Link>
      </div>

      {/* Nav Items */}
      <div className="flex-1 py-4 overflow-y-auto">
        <nav className="space-y-1 px-2">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-all duration-150 relative z-10 ${
                isActive(item.path)
                  ? 'bg-primary/10 text-primary border-l-2 border-l-primary'
                  : 'text-muted-foreground hover:text-white hover:bg-secondary'
              }`}
              data-testid={`nav-${item.path.slice(1)}`}
            >
              <item.icon className={`w-5 h-5 ${collapsed ? 'mx-auto' : ''} pointer-events-none`} />
              {!collapsed && <span className="pointer-events-none">{item.label}</span>}
            </Link>
          ))}
        </nav>
      </div>

      {/* Bottom Section */}
      <div className="border-t border-border p-4 space-y-3">
        {/* Language Selector */}
        {!collapsed && (
          <Select value={user?.language || 'ja'} onValueChange={handleLanguageChange}>
            <SelectTrigger className="w-full input-cyber h-9" data-testid="nav-language-selector">
              <Globe className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ja">日本語</SelectItem>
              <SelectItem value="en">English</SelectItem>
            </SelectContent>
          </Select>
        )}

        {/* User Info */}
        <div className={`flex items-center gap-3 ${collapsed ? 'justify-center' : ''}`}>
          <Avatar className="w-8 h-8 bg-primary/20">
            <AvatarFallback className="bg-primary/20 text-primary text-sm">
              {user?.name?.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          )}
        </div>

        {/* Logout */}
        <Button
          variant="ghost"
          onClick={handleLogout}
          className={`w-full justify-start text-muted-foreground hover:text-destructive ${collapsed ? 'px-3' : ''}`}
          data-testid="logout-button"
        >
          <LogOut className={`w-4 h-4 ${collapsed ? 'mx-auto' : 'mr-2'}`} />
          {!collapsed && t.nav.logout}
        </Button>

        {/* Collapse Button (Desktop only) */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setCollapsed(!collapsed)}
          className="w-full hidden lg:flex justify-center"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile Menu Button */}
      <Button
        variant="ghost"
        size="sm"
        className="fixed top-4 left-4 z-50 lg:hidden"
        onClick={() => setMobileOpen(!mobileOpen)}
        data-testid="mobile-menu-button"
      >
        <Menu className="w-5 h-5" />
      </Button>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar - Desktop */}
      <aside
        className={`hidden lg:flex fixed inset-y-0 left-0 z-30 ${
          collapsed ? 'w-16' : 'w-64'
        } bg-card border-r border-border flex-col transition-all duration-200`}
        data-testid="desktop-sidebar"
      >
        <NavContent />
      </aside>

      {/* Sidebar - Mobile (only rendered when open) */}
      {mobileOpen && (
        <aside
          className="fixed lg:hidden inset-y-0 left-0 z-50 w-64 bg-card border-r border-border flex flex-col"
          data-testid="mobile-sidebar"
        >
          <NavContent />
        </aside>
      )}

      {/* Main Content */}
      <main
        className={`flex-1 transition-all duration-200 ${
          collapsed ? 'lg:ml-16' : 'lg:ml-64'
        }`}
      >
        <div className="p-4 pt-16 lg:pt-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default MainLayout;
